import { useMemo } from 'react';
import type { AiCampaignForm, AiPreviewData } from '@/features/campaigns/types/campaign';
import { formatDisplayDate } from './aiPreviewHelpers';

/** Pure metrics derived from the current AI plan snapshot (dates, screens, budget). */
export function useAiPreviewDerived(current: AiPreviewData, formData: AiCampaignForm) {
  const summary = current.summary;
  const estimatedCost = current.estimatedCost;
  const preview = current.preview;
  // Pricing/analytics only ship in screensDetail, so fold them onto the matching screen.
  const screens = useMemo(() => {
    const list = current.screens ?? [];
    const detail = current.screensDetail ?? [];
    if (!detail.length) return list;
    return list.map((screen) => {
      const match = detail.find(
        (d) =>
          (d._id && screen._id && String(d._id) === String(screen._id)) ||
          (d.screenName && d.screenName === screen.screenName),
      );
      if (!match) return screen;
      return {
        ...match,
        ...screen,
        analytics: screen.analytics ?? match.analytics,
        address: screen.address ?? match.address,
        basePrice: screen.basePrice ?? match.basePrice,
        pricing: screen.pricing ?? match.pricing,
      };
    });
  }, [current.screens, current.screensDetail]);

  const screenNameById = useMemo(() => {
    const map: Record<string, string> = {};
    screens.forEach((s) => {
      const id = String(s._id || '');
      if (id) map[id] = s.screenName || id;
    });
    preview?.billboardsData?.forEach((b) => {
      if (b.billboardId) {
        map[b.billboardId] =
          b.billboardName || map[b.billboardId] || b.billboardId;
      }
    });
    (current.slotsDetail || []).forEach((slot) => {
      const id = String(slot.billboardId || '');
      if (id && !map[id]) {
        map[id] = id;
      }
    });
    return map;
  }, [screens, preview, current.slotsDetail]);

  const namesForSlot = useMemo(() => {
    const byKey: Record<string, string[]> = {};
    (current.slotsDetail || []).forEach((d) => {
      const key = `${d.date}|${d.hour}`;
      const id = String(d.billboardId || '');
      const name = screenNameById[id] || id;
      if (!name) return;
      if (!byKey[key]) byKey[key] = [];
      if (!byKey[key].includes(name)) byKey[key].push(name);
    });
    return byKey;
  }, [current.slotsDetail, screenNameById]);

  const fallbackScreenNames = useMemo(
    () => screens.map((s) => s.screenName).filter((n): n is string => Boolean(n)),
    [screens],
  );

  const startDate = preview?.startDate || formData.startDate || '';
  const endDate = preview?.endDate || formData.endDate || '';
  const dateRange =
    startDate && endDate
      ? `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`
      : 'N/A';

  const durationSec =
    summary?.customDuration ??
    preview?.customDuration ??
    (formData.adDuration ? Number(formData.adDuration) : null);

  const totalScreens = summary?.totalScreens ?? screens.length;
  const totalRepetitions = summary?.totalRepetitions;
  const planBudget = Number(formData.budget || current.payload?.budget || 0) || null;
  
  const rawAmountWithTax =
    summary?.amountWithTax ??
    estimatedCost?.finalPayableAmount ??
    estimatedCost?.total ??
    null;

  const rawBaseAmount =
    summary?.totalBaseAmount ??
    estimatedCost?.baseAmount ??
    summary?.amountWithoutTax ??
    estimatedCost?.amountWithoutTax ??
    null;

  const couponDiscount =
    current.coupon?.discountAmount ??
    estimatedCost?.discountAmount ??
    summary?.discountAmount ??
    null;

  const appliedDiscount = Number(
    couponDiscount ?? summary?.taxBreakdown?.totalDiscount ?? 0,
  );

  const totalTax = Number(summary?.totalTax ?? estimatedCost?.totalTax ?? 0);

  // Gross Campaign cost before coupon discount
  const amountWithoutTax = (() => {
    const base = Number(rawBaseAmount ?? 0);
    const withTax = Number(rawAmountWithTax ?? 0);
    if (base > 0) {
      if (appliedDiscount > 0 && base < withTax + appliedDiscount && base === withTax) {
        return withTax + appliedDiscount;
      }
      return base;
    }
    if (withTax > 0) {
      return withTax + appliedDiscount;
    }
    return 0;
  })();

  // Net payable amount after coupon discount and tax
  const amountWithTax = Math.max(
    0,
    amountWithoutTax - appliedDiscount + totalTax,
  );

  const remaining = estimatedCost?.remaining ?? null;
  const utilization = Number(estimatedCost?.utilizationPercentage ?? 0);

  const taxRowsRaw = (() => {
    const dist = (
      summary?.taxDistribution ??
      estimatedCost?.taxDistribution ??
      []
    ).filter((item) => item != null);

    if (dist.length) {
      return dist.map((item) => ({
        label: item.percentage
          ? `${item.role ?? 'Tax'} (${item.percentage}%)`
          : (item.role ?? 'Tax'),
        value: Number(item.totalAmount ?? 0),
      }));
    }

    const breakdown = summary?.taxBreakdown ?? estimatedCost?.taxBreakdown;
    const entries = Object.entries(breakdown ?? {}).filter(
      ([key, value]) =>
        key.toLowerCase() !== 'totaldiscount' && value != null,
    );

    if (entries.length) {
      return entries.map(([key, value]) => ({
        label: key.replace(/_/g, ' ').toUpperCase(),
        value: Number(value),
      }));
    }

    return [
      {
        label: 'GST',
        value: Number(summary?.totalTax ?? estimatedCost?.totalTax ?? 0),
      },
    ];
  })();

  // After a coupon the GST is charged on the reduced amount, but the rows can still show the
  // full-price figures. Scale them so the rows add up to the tax actually charged.
  const taxRowsTotal = taxRowsRaw.reduce((sum, row) => sum + row.value, 0);
  const taxRows =
    totalTax > 0 && taxRowsTotal > 0 && Math.abs(taxRowsTotal - totalTax) > 0.01
      ? taxRowsRaw.map((row) => ({ ...row, value: (row.value / taxRowsTotal) * totalTax }))
      : taxRowsRaw;

  return {
    summary,
    estimatedCost,
    preview,
    screens,
    screenNameById,
    namesForSlot,
    fallbackScreenNames,
    dateRange,
    durationSec,
    totalScreens,
    totalRepetitions,
    planBudget,
    amountWithTax,
    amountWithoutTax,
    couponDiscount,
    remaining,
    utilization,
    appliedDiscount,
    taxRows,
    coupon: current.coupon,
  };
}
